import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GS1ButtonDirective, GS1FormModule } from '@gs1france/gs1-components-library';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CodeService } from '@services/code.service';
import { CompanyService } from '@services/company.service';
import { catchError, forkJoin, Observable, of, take, tap } from 'rxjs';

@Component({
  selector: 'app-side-menu-certificate',
  templateUrl: './side-menu-certificate.component.html',
  styleUrls: ['./side-menu-certificate.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, MatTooltipModule, GS1ButtonDirective, GS1FormModule]
})
export class SideMenuCertificateComponent {
  private readonly companyService = inject(CompanyService);
  private readonly codeService = inject(CodeService);
  private readonly translate = inject(TranslateService);

  readonly frSelected = signal<boolean>(true);
  readonly enSelected = signal<boolean>(false);
  readonly loading = signal<boolean>(false);

  readonly company = computed(() => this.companyService.company());
  // the single app-wide company fetch (started in ConnectComponent) may still be in flight when this menu renders
  readonly companyLoading = computed(() => this.companyService.companyLoading());

  readonly isEnabled = computed<boolean>(() => {
    const hasLanguage = this.frSelected() || this.enSelected();
    const comp = this.company();
    const hasGcpAndGln = comp != null && comp.gcp != null && comp.gln != null;
    const isGoodPayer = comp != null && (comp.isBadPayer !== true || comp.isBadPayerException === true);
    return Boolean(hasLanguage && hasGcpAndGln && isGoodPayer);
  });

  readonly disabledReason = computed<string>(() => {
    if (this.isEnabled() || this.companyLoading()) {
      return '';
    }
    const comp = this.company();
    if (!comp?.gcp) {
      return this.translate.instant('nav.certificate.noGcpMessage');
    }
    if (comp.isBadPayer === true && comp.isBadPayerException !== true) {
      return this.translate.instant('nav.certificate.badPayerMessage');
    }
    return '';
  });

  toggleFr(): void {
    this.frSelected.update((val) => !val);
  }

  toggleEn(): void {
    this.enSelected.update((val) => !val);
  }

  download(): void {
    if (!this.isEnabled() || this.loading()) {
      return;
    }

    const downloads: Observable<Blob>[] = [];
    if (this.frSelected()) {
      downloads.push(this.codeService.downloadCertificate('FR'));
    }
    if (this.enSelected()) {
      downloads.push(this.codeService.downloadCertificate('EN'));
    }

    if (downloads.length === 0) {
      return;
    }

    this.loading.set(true);

    forkJoin(downloads)
      .pipe(
        take(1),
        tap(() => this.loading.set(false)),
        catchError((error) => {
          console.error(error);
          this.loading.set(false);
          return of(null);
        })
      )
      .subscribe();
  }
}
